<script>
export default {
    page() {
        const title = 'Browse Genres'
        const description = 'Browse FRISKY by genre — house, techno, progressive, deep, and more.'
        const image = `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'website' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import { useMessages } from '@composables/use-messages'
import Layout from '@layouts/main-layout'
import GenreGridBlock from '@components/genre/genre-grid-block'

const router = useRouter()
const store = useStore()
const getMessageText = useMessages()

// Guards against a second follow being sent while the first is still in flight.
const isReady = ref(true)

const isLoggedIn = computed(() => store.getters['auth/loggedIn'])

const followedGenreIds = computed(() => store.getters['favorites/genreIds'])

const activeGenres = computed(() => store.getters['genres/activeGenres'])

async function handleToggleFollow(genre) {
    if (!isLoggedIn.value) {
        router.push({ name: 'login' })
        return
    }

    if (!isReady.value || !genre) return

    isReady.value = false

    try {
        const isFollowing = followedGenreIds.value.includes(genre.id)
        await store.dispatch(isFollowing ? 'favorites/remove' : 'favorites/add', {
            type: 'genres',
            id: genre.id,
        })
        store.dispatch('appearance/handleNotification', {
            message: isFollowing ? 'Genre Unfollowed' : 'Following Genre',
            type: 'success',
        })
    } catch (error) {
        console.error('Error toggling genre follow:', error)
        store.dispatch('appearance/handleNotification', {
            message: getMessageText('SOMETHING_WENT_WRONG'),
            type: 'warning',
        })
    } finally {
        isReady.value = true
    }
}
</script>

<template>
    <Layout>
        <BaseContent title="Genres & Moods">
            <BaseSection padded>
                <GenreGridBlock
                    :genres="activeGenres"
                    :followed-genre-ids="followedGenreIds"
                    @toggle-follow="handleToggleFollow"
                />
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
</style>
